/* 엔진 실행. 추론은 MCP 호스트 모델의 것이고 네 구간이다: 1 풀이와 그림 계획(문법 강제 없이 글로 풀이하고, 색칠도구 목록과 각 도구가 받는 것을 보며 어떻게 그릴지와 쓸 도구까지 같은 글에서 정한다), 2 풀이 글에서 값 옮기기(JSON: 기호 값, 그림 유무, 답, 계획의 도구 이름), 3 입력 개체(식, 좌표, 슬라이더 호출), 4 연계 사용법과 args(계획한 도구의 사용법만 받아 단계별 호출 JSON).
 * 호출 JSON(tools/contract.ts)을 코드가 대조하고 mathcore 스케치북에 개체로 세운다. 접선, 교점, 측정처럼 mathcore 가 계산하는 값은 모델이 계산한 좌표를 찍지 않고 도구로 만든다. 어휘는 색칠도구(dsl.json)뿐이다 */
import { seconds } from './model';
import type { ChatMessage, ModelPort } from './model';
import { INPUT_TOOLS, LINK_PHASES, LINK_TOOLS, NAMES, callsSchema, drawCalls, inPalette, menuText, paletteText, readPicked, readSymbols, registryOf, standingIds, standingRefs, withProducers, standingObjects, splitGiven } from './tools';
import type { Drawn, MathcoreApp } from './tools';
import { exprGrammarRule } from './system';
import { readCurveShape } from './expr';
import { FIGURE_DECLARATION_RULE, ITEM_FORMAT_RULE, SOLUTION_FORMAT, VARIANT_ELEMENTS, WRITING_RULE, readDeclarations, splitWritten } from './solution';
import { kindLabel } from './tools/dsl';
import { readMeasurements, similarityPercent, verifyAgainstMeasured } from './measure';
import { buildTarget } from './target';
import { independentValue, judgeIndependent } from './independent';
import { answerFormatIssue, answerText, givenWith, judge, problemFigure, problemPointNames, readSolved, solveSchema, withValues } from './solve';
import type { Acceptance, Solved } from './solve';
import { describeSymbols, problemSymbols, restoreEscapes } from './symbols';
import { normalizeDelimiters } from './segments';
import { cleanModelText } from './symbols';
import { backgroundKnowledge } from './knowledge';
import { checkRelations, provenanceOf } from './relations';
import type { Relation } from './relations';

export type EntryKind = 'user' | 'think' | 'assistant' | 'call' | 'result' | 'error' | 'meta';

export interface Meta {
  mode?: 'original' | 'variant';
  problem?: string;
  /** 변형일 때 원본 문항 */
  variantText?: string;
  /** 변형이 검증을 통과하지 못한 사유. 문항 패널에는 이것만 보인다 */
  rejected?: string;
  changes?: string[];
  similarity?: number;
  independent?: { verdict: 'ok' | 'mismatch' | 'none'; detail: string };
  answer?: string;
  verdict?: string;
  /** 끝 관문 판정과 사유. 원문과 변형 모두 */
  acceptance?: Acceptance;
  acceptanceReason?: string;
  measured?: string[];
  figure?: string;
  objectCount?: number;
  failureCount?: number;
  elapsed?: string;
  /** 생성 단계마다 호스트 모델이 쓴 한두 문장 설명(pipeline.ts 가 단계 캡처에 붙인다) */
  stageCaptions?: string[];
  /** 풀이 단계마다 선언한 개체 이름(그림 선언). 코드가 단계 캡처와 매핑을 이것으로 정한다 */
  declared?: string[][];
  /** 풀이 단계(1부터) → 그림 단계(1부터) 매핑. 해설과 삽화의 연결(기획 4항) */
  stepMap?: { step: number; stage: number }[];
  /** 추론과 생성의 타임라인(시작부터 ms) */
  timeline?: { label: string; at: number }[];
  /** 배경 지식: 유사 기출 참고 수 */
  background?: { similar: number; experience: { recipes: number; failures: number; notes: number; total: number } };
  /** 관계 단언 검사 결과 */
  relations?: { kind: string; a: string; b: string; c: string | null; verdict: string; detail: string }[];
  /** 고정 보고: 스키마와 지시문의 sha256, 제시된 식, 문항의 기호 */
  fixed?: { schemaSha: string; solveSchemaSha?: string; inputPromptSha?: string; writePromptSha?: string; promptSha: string; given: string[]; textSymbols?: string[]; symbols?: string[]; tools?: string[] };
}

export interface Hooks {
  push: (kind: EntryKind, text: string) => void;
  replaceThinking: (text: string) => void;
  setStatus: (text: string) => void;
  setSolution: (markdown: string) => void;
  setMeta: (part: Partial<Meta>) => void;
  /** 생성 뒤 호출. 단계 이름을 받고 카메라 맞춤과 단계 캡처가 끝날 때까지 기다린다 */
  afterExecute?: (label: string) => void | Promise<void>;
  /** 풀이 단계가 선언한 개체가 전부 선 순간의 캡처(선언 이름 경계 캡처). step 은 1부터 */
  captureStep?: (step: number, label: string, names: string[]) => Promise<void>;
  /** 도구 호출 하나가 선 뒤의 캡처(재생 프레임). names 는 그 순간 서 있는 이름 전부 */
  captureFrame?: (names: string[], label: string) => Promise<void>;
  /** 지금 그림판(base64 PNG). 다음 요청에 직전 화면을 실어 보낸다 */
  latestShot?: () => string;
  /** 모델 포트. 엔진은 이 하나로만 모델에게 묻는다. MCP 호스트가 응답하는 대기 포트(pipeline.ts) */
  model?: ModelPort;
}

// 생성 토큰 상한(풀이, 변형). 사용자가 준 레퍼런스(KSAT-AI-Benchmark 프로토콜 max_output_tokens 65536)의 값. 모델이 작다고 줄이지 않는다
const MAX_GENERATE = 65536;
// 사고를 켠 풀이와 변형의 생성 토큰 상한. 사고 토큰도 같은 상한을 쓴다
const MAX_GENERATE_THINK = 65536;
// 되묻기 없음. 반려와 실패는 모델에게 되돌리지 않고 판정 사유로 남긴다(색칠도구를 정한 뒤 못 쓰는 것은 우리 버그다, 사용자 명령 2026-10-19)
const FEEDBACK_ROUNDS = 0;
// 생성 토큰 상한(사용법과 args). 같은 레퍼런스 값
const MAX_GENERATE_TOOLS = 65536;

const VARIANT_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    problem: { type: 'string' },
    changes: { type: 'array', items: { type: 'string' }, minItems: 1, maxItems: 4 },
  },
  required: ['problem', 'changes'],
  additionalProperties: false,
};

/** 잰 값과 답이 다를 때의 되먹임 스키마: 고칠 호출과 고친 답 */
function verifySchema(given: string[], names: string[], tools: string[], standing: readonly { id: string; kind: string }[]): Record<string, unknown> {
  const base = callsSchema(given, names, tools, true, standing);
  return {
    ...base,
    properties: { ...(base.properties as Record<string, unknown>), value: { type: 'number', description: '다시 확인한 답의 수. 답이 맞으면 같은 값' } },
    required: [...(base.required as string[]), 'value'],
  };
}

/** 1 풀이 작문과 그림 계획: 문법 강제 없이 풀이를 끝까지 글로 쓰고, 그 풀이로 이 문제를 스케치북에 어떻게 그려 내고 어떤 색칠도구를 쓸지까지 같은 글에서 정한다. 도구 목록과 각 도구가 받는 것을 이 단계가 받는다 */
function writePrompt(): string {
  return [
    '너는 대한민국 수능 수학 문항을 풀고, 그 문제를 mathcore 스케치북에 어떻게 그려 낼지까지 계획한다. JSON 이 아니라 글로 답한다.',
    `- 글은 세 절로 쓴다. 첫 절은 「${SOLUTION_FORMAT.steps}」 머리로 시작한다. ${SOLUTION_FORMAT.stepsRule} ${FIGURE_DECLARATION_RULE}`,
    `- 둘째 절은 「${SOLUTION_FORMAT.quick}」 머리로 시작한다. ${SOLUTION_FORMAT.quickRule}`,
    `- 셋째 절은 「${SOLUTION_FORMAT.plan}」 머리로 시작한다. 이 문제를 어떻게 그리고 무엇을 재서 답과 대조할지 순서대로 쓴다. 쓸 색칠도구는 아래 「색칠도구」 목록에서 고른다.`,
    '  - 도구마다 「받는 것」이 있다. 받는 개체가 먼저 서 있어야 한다. 길이, 기울기, 설명선은 선이, 넓이는 원, 다각형, 타원이 먼저 필요하다. 받는 개체를 세우는 도구도 함께 고른다.',
    '  - 곡선(식), 좌표가 정해진 점, 기호를 묶는 슬라이더는 앞 단계의 입력 도구(algebraric, point.basic, etc.slider)가 세운다. 계획에는 적지 않는다.',
    '  - 접선, 교점, 길이, 넓이처럼 mathcore 가 계산해 주는 값은 풀이가 계산한 좌표를 찍지 않고 그 도구로 만든다. 문항이 구하라는 길이, 넓이, 각, 기울기는 측정 도구로 잰다.',
    '  - 곡선과 축 사이, 두 곡선 사이의 넓이는 measure.area 로 못 잰다(다각형, 원, 타원만). 정적분 식을 $\\int_{a}^{b} … dx$ 로 풀이에 적고 그 식을 algebraric 에 주면 값이 선다. 답이 p+q, 10a 처럼 잰 값에서 유도되면 그 바탕 값(예: 넓이 27/4)을 풀이에 식으로 적는다. 그 값이 그림에서 재는 값이다.',
    '  - 마지막 줄에 「도구: 이름, 이름」을 쓴다. 목록에 있는 이름만 쓰고 같은 이름은 한 번만 쓴다. 필요한 도구가 없으면 「도구: 없음」.',
    '- 함수의 식, 기호의 값, 점의 좌표를 구하면 계산 과정과 함께 그 값을 쓴다. 문항에 없고 구하지도 않은 값은 쓰지 않는다.',
    '- 풀이의 마지막 줄(그림 계획 앞)에 「답: 값」을 쓴다. 객관식이면 고른 선지(①~⑤)와 그 값을 함께 쓴다.',
    `- ${WRITING_RULE}`,
    '',
    '## 색칠도구 (이름: 한글 이름 - 설명 → 만드는 개체 · 받는 것)',
    menuText(LINK_TOOLS),
  ].join('\n');
}

/** 2 값 옮기기: 풀이 글에서 값(기호 값, 그림 유무, 답)을 JSON 으로 옮긴다. 풀이 글에 없는 값은 만들지 않는다 */
function extractPrompt(symbols: string[]): string {
  return [
    '너는 수능 수학 문항의 풀이 글에서 값을 옮긴다. 풀이 글에 없는 값은 만들지 않는다. JSON 으로만 답한다.',
    '- figure: 문항에 그릴 그림(좌표평면의 곡선, 점, 도형)이 있으면 「있음」, 수열이나 경우의 수처럼 식만 다루면 「없음」.',
    '- target: 문항이 구하라는 양(넓이, 길이, 기울기, 각)을 선언한다. 엔진이 그 선언으로 측정 개체를 직접 세워 잰다. 그림 선언의 이름으로 적는다.',
    `- values: 문항이 쓴 기호의 값. 풀이 글이 정한 수 하나, 풀이 글이 정하지 못했으면 null 을 준다. 기호: ${describeSymbols(symbols).join(', ') || '(없음, 빈 객체)'}.`,
    '- choice: 풀이 글이 고른 선지(①~⑤), 단답형이면 빈 문자열. value: 풀이 글의 답. 수로 옮기거나 풀이 글의 식(LaTeX)을 그대로 옮긴다. 수로의 계산은 코드가 한다. 답은 「그림 계획」 앞의 「답:」 줄이다.',
    '- tools: 풀이 글의 「그림 계획」 마지막 줄 「도구:」에 적힌 색칠도구 이름. 글에 없는 이름은 만들지 않는다. 「도구: 없음」이면 빈 배열.',
  ].join('\n');
}

/** 3 입력 개체: 값만 받는 도구(식, 좌표, 슬라이더)로 곡선과 점을 세운다. 도구가 계산하는 것은 다음 단계의 몫이다 */
function inputPrompt(given: string[], names: string[], tools: string[]): string {
  return [
    '너는 풀이한 수능 수학 문항을 mathcore 스케치북에 그린다. 이 단계는 값만 받는 개체를 세운다: 문항과 풀이에 제시된 식의 곡선, 좌표가 정해진 점, 값 없는 기호를 묶는 슬라이더. calls 에 호출을 순서대로 적는다. tool 은 아래 목록의 이름, args 는 그 도구의 인자다. 선택 인자는 null 로 둘 수 있다.',
    '- expression 은 식을 새로 쓰지 않고 아래 「제시된 식」 중 하나를 그대로 고른다. 그 식에 조건(예: |x| \\le 1 일 때)이 붙어 있으면 condition 에 그 조건 식을 고른다.',
    names.length
      ? `- 식에 든 기호는 symbols 에 값을 준다(풀이 글이 정한 값). 값을 정하지 못한 기호가 식에 남으면 슬라이더로 먼저 세워 그 이름으로 묶고, 슬라이더는 그 이름을 쓰는 식보다 앞에 적는다. 기호: ${describeSymbols(names).join(', ')}.`
      : '- 이 문항에는 값을 선언할 기호가 없다. symbols 는 빈 배열이고 슬라이더는 쓰지 않는다.',
    '- mathcore 가 도구로 계산해 주는 것(곡선 위의 점, 접점, 교점, 접선, 도형, 길이, 넓이)은 다음 단계에서 도구로 만든다. 여기서는 풀이가 계산한 좌표를 점으로 찍지 않고, 문항이 좌표를 직접 준 점과 도구로 만들 수 없는 점만 점 도구로 적는다. 그릴 것이 없으면 빈 배열.',
    '',
    '## 제시된 식 (문항과 풀이에 있는 것)',
    ...given.map((g, i) => `${i + 1}. ${g}`),
    '',
    exprGrammarRule(),
    '',
    '## 색칠도구 사용법',
    paletteText(tools),
  ].join('\n');
}

/** 5 연계 사용법과 args: 취합한 도구의 사용법만 받고 호출 JSON 하나를 낸다 */
function argsPrompt(given: string[], names: string[], tools: string[], phase: string): string {
  return [
    `너는 math2d 색칠도구로 문항의 그림을 그린다. 이번 단계는 「${phase}」 단계다. 앞 단계가 세운 개체는 사용자 글의 「이미 선 개체」에 있다. 취합한 연계 도구의 사용법을 아래에 준다. calls 에 호출을 순서대로 적는다. tool 은 아래 목록의 이름, args 는 그 도구의 인자다. 개체를 가리키는 인자는 「이미 선 개체」의 id 를 쓴다. 이 JSON 에서 먼저 세운 개체는 그 name 으로 가리킨다. 선택 인자는 null 로 둘 수 있다.`,
    '- expression 은 식을 새로 쓰지 않고 아래 「제시된 식」 중 하나를 그대로 고른다. 그 식에 조건이 붙어 있으면 condition 에 그 조건 식을 고른다. 문항이 구하라는 길이, 넓이, 각, 기울기는 measure 도구로 잰다. 곡선과 축 사이나 두 곡선 사이의 넓이는 제시된 식의 정적분 식을 algebraric 으로 세워 얻는다.',
    '- 개체 자리에 맞는 개체가 「이미 선 개체」에 없으면 그 자리에 뜻만 적는다. 점은 {"x":수,"y":수,"on":null}(곡선 위의 점이면 "on" 에 그 곡선의 id), 선은 {"through":[점,점]}, 곡선은 {"expression":제시된 식,"condition":조건 또는 null}, 원은 {"center":점,"radius":수}. 그 개체를 세우는 도구와 순서는 코드가 정한다. 세우지 않은 이름은 가리키지 않는다.',
    `- 식에 든 기호는 symbols 에 값을 준다. 값 없는 기호가 식에 남으면 그 호출은 반려된다. 기호: ${describeSymbols(names).join(', ') || '(없음)'}. args 에는 그 도구에 있는 인자만 쓴다.`,
    '',
    '## 제시된 식 (문항과 풀이에 있는 것)',
    ...given.map((g, i) => `${i + 1}. ${g}`),
    '',
    exprGrammarRule(),
    '',
    '## 색칠도구 사용법 (취합한 것)',
    paletteText(tools),
  ].join('\n');
}

function variantPrompt(elements: string[]): string {
  return [
    '기준 기출 문항 하나를 받아 같은 구조, 같은 묻는 것의 새 문항을 한 단계만 변형해 만든다. JSON 으로만 답한다.',
    `- 바꾸는 것: ${elements.length ? elements.join(', ') : `${VARIANT_ELEMENTS.join(', ')} 가운데 둘에서 넷`}. 그 밖은 원본 그대로.`,
    '- problem: 새 문항 원문(한국어, 식은 $…$ LaTeX). 답이 깔끔한 값이 되게 수를 고른다. 이 문항은 그림이 서고 잰 값이 답과 맞을 때만 채택된다.',
    ITEM_FORMAT_RULE,
    '- 「기술 결합」을 고르면 다른 단원의 개념 하나(접선, 넓이, 대칭, 수열의 합, 확률 같은 것)를 원본 구조에 한 단계 더 결합해 깊게 만든다. 묻는 것과 답의 형식은 유지한다.',
    '- changes: 바꾼 것을 한국어로 하나에서 넷.',
  ].join('\n');
}

interface Asked { raw: Record<string, unknown>; ms: number; tokens: number | undefined }

/** 한 번 묻는다. 토큰 잘림과 JSON 불량은 던지지 않고 사유로 돌려준다 */
async function askOnce(messages: ChatMessage[], schema: Record<string, unknown>, think: boolean, signal: AbortSignal, hooks: Hooks, label: string, numPredict: number): Promise<{ asked: Asked } | { problem: string; head: string }> {
  const started = Date.now();
  const answer = await modelPort(hooks)(messages, { label, signal, think, format: schema, numPredict }, (t) => hooks.replaceThinking(t), (c) => hooks.setStatus(`${label} 수신 중 ${c.length}자`));
  if (answer.doneReason === 'length') return { problem: `${label}이 토큰 한도에서 잘렸다(프롬프트 ${answer.promptEvalCount ?? '?'} 토큰, 생성 ${answer.evalCount ?? '?'} 토큰 = 곱씹기 ${answer.thinking.length}자 + 본문 ${answer.content.length}자)`, head: answer.content.slice(0, 600) };
  let value: unknown;
  try { value = restoreEscapes(JSON.parse(answer.content)); } catch (error) { return { problem: `${label} JSON 을 읽지 못했다: ${(error as Error).message}`, head: answer.content.slice(0, 300) }; }
  if (!value || typeof value !== 'object') return { problem: `${label} 이 객체가 아니다`, head: answer.content.slice(0, 300) };
  return { asked: { raw: value as Record<string, unknown>, ms: Date.now() - started, tokens: answer.evalCount } };
}

/** 모델 포트. 화면과 서버는 모델을 부르지 않으므로 포트가 없으면 엔진이 멈춘다 */
function modelPort(hooks: Hooks): ModelPort {
  if (!hooks.model) throw new Error('모델 포트가 없다. MCP 호스트가 pipeline_start 로 시작한다');
  return hooks.model;
}

/** 묻고, 잘림이나 JSON 불량이면 그 사실을 돌려주며 같은 단계를 한 번 더 묻는다 */
async function askJson(messages: ChatMessage[], schema: Record<string, unknown>, think: boolean, signal: AbortSignal, hooks: Hooks, label: string, numPredict = MAX_GENERATE): Promise<Asked> {
  const first = await askOnce(messages, schema, think, signal, hooks, label, numPredict);
  if ('asked' in first) return first.asked;
  hooks.push('error', `${first.problem}. 본문 머리: ${first.head}`);
  hooks.setStatus(`${label} 다시 요청 (호스트 모델)`);
  const retry: ChatMessage[] = [
    ...messages,
    { role: 'assistant', content: first.head },
    { role: 'user', content: `앞 답은 쓸 수 없었다: ${first.problem}. 같은 스키마의 JSON 하나만, 더 짧게 다시 낸다. 호출은 꼭 필요한 것만 적는다.` },
  ];
  const second = await askOnce(retry, schema, think, signal, hooks, `${label} 재요청`, numPredict);
  if ('asked' in second) return second.asked;
  throw new Error(`${second.problem}. 본문 머리: ${second.head}`);
}

/** 문법 강제 없이 글로 묻는다. 토큰 잘림은 던지지 않고 로그에 남기며, 받은 글까지로 다음 단계를 한다. 빈 글이면 던진다 */
async function askText(messages: ChatMessage[], think: boolean, signal: AbortSignal, hooks: Hooks, label: string, numPredict: number): Promise<{ text: string; ms: number; tokens: number | undefined }> {
  const started = Date.now();
  const answer = await modelPort(hooks)(messages, { label, signal, think, numPredict }, (t) => hooks.replaceThinking(t), (c) => hooks.setStatus(`${label} 수신 중 ${c.length}자`));
  const text = answer.content.trim();
  if (answer.doneReason === 'length') hooks.push('error', `${label}이 토큰 한도에서 잘렸다(생성 ${answer.evalCount ?? '?'} 토큰). 받은 글까지로 다음 단계를 한다`);
  if (!text) throw new Error(`${label}이 빈 글이다`);
  return { text, ms: Date.now() - started, tokens: answer.evalCount };
}

/** 지금 화면에 선 개체(이름, 종류, 유효). 되먹임에 상태로 준다 */
function sceneState(app: MathcoreApp): string[] {
  const registry = registryOf(app);
  // 별칭과 요청한 이름만 보인다. mathcore 의 개체 번호와 자동 이름은 실행마다 달라 모델 입력에 싣지 않는다
  return standingObjects(app).map((o) => { const label = registry.labelOf(o.id); return `- ${registry.aliasOf(o.id)}${label ? ` ${label}` : ''} (${o.type})`; });
}

/** 화면 그림(canvas 층을 합친 PNG, base64 머리 없이). 되먹임에 그림으로 준다. 못 찍으면 빈 배열과 사유 */
function captureCanvas(): { images: string[]; note: string } {
  try {
    const layers = Array.from(document.querySelectorAll<HTMLCanvasElement>('.canvas canvas'));
    if (!layers.length) return { images: [], note: '캔버스 층이 없다' };
    const out = document.createElement('canvas'); out.width = layers[0].width; out.height = layers[0].height;
    const g = out.getContext('2d'); if (!g) return { images: [], note: '2d 컨텍스트를 못 얻었다' };
    g.fillStyle = '#fff'; g.fillRect(0, 0, out.width, out.height);
    for (const c of layers) g.drawImage(c, 0, 0);
    const data = out.toDataURL('image/png');
    return { images: [data.slice(data.indexOf(',') + 1)], note: '' };
  } catch (error) { return { images: [], note: error instanceof Error ? error.message : String(error) }; }
}

async function sha256(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** 생성 결과를 로그에 적는다 */
function logDrawn(hooks: Hooks, drawn: Drawn): void {
  for (const k of drawn.skipped) hooks.push('meta', k);
  for (const r of drawn.rejected) hooks.push('error', `대조에서 거름 · ${r.tool}: ${r.detail}`);
  for (const r of drawn.results) hooks.push(r.ok ? 'result' : 'error', `${r.method} → ${r.detail}`);
}

/** 호출 한 번의 결과: 성공 여부, 도구, 이름(없으면 빈 글자) */
interface Event { ok: boolean; tool: string; name: string }

function nameOfArgs(json: string): string {
  try { const n = JSON.parse(json).name; return typeof n === 'string' ? n : ''; } catch { return ''; }
}

const eventsOf = (d: Drawn): Event[] => [
  ...d.rejected.map((r) => ({ ok: false, tool: r.tool, name: r.name })),
  ...d.results.map((r) => ({ ok: r.ok, tool: r.call.function.name, name: nameOfArgs(r.call.function.arguments) })),
];

/** 아직 풀리지 않은 실패 수. 실패한 호출은 뒤에 같은 도구가 같은 이름(조각이면 이름_번호)으로 성공해야 풀린 것이다. 되먹임 회차가 지나도 앞 실패를 잊지 않는다 */
function unresolvedFailures(events: Event[]): number {
  return events.filter((e, i) => !e.ok && !events.slice(i + 1).some((later) => later.ok && later.tool === e.tool && (later.name === e.name || (e.name !== '' && later.name.startsWith(`${e.name}_`))))).length;
}

/** 되먹임에 돌릴 반려 문장. 대조 반려와 실행 실패 전부, 그리고 읽지 못한 기호 선언의 사유 */
function complaintsOf(drawn: Drawn, symbolIssues: string[] = []): string[] {
  return [...drawn.rejected.map((r) => `${r.tool}: ${r.detail}`), ...drawn.results.filter((r) => !r.ok).map((r) => `${r.call.function.name}: ${r.detail}`), ...symbolIssues.map((s) => `symbols: ${s}`)];
}

/** 반려되거나 실행에 실패한 호출의 도구 이름. 취합에 없던 도구라도 고쳐 다시 부르려면 사용법이 필요하다 */
function failedToolsOf(d: Drawn): string[] {
  return [...d.rejected.map((r) => r.tool), ...d.results.filter((r) => !r.ok).map((r) => r.call.function.name)];
}

/** 되먹임 한 턴의 사용자 메시지: 사유, 선 개체, 화면 그림 */
function feedbackTurn(app: MathcoreApp, hooks: Hooks, body: string): ChatMessage {
  const standing = sceneState(app);
  const shot = captureCanvas();
  if (shot.note) hooks.push('meta', `화면 그림을 못 찍어 그림 없이 되먹인다: ${shot.note}`);
  return { role: 'user', content: `${body}\n\n## 지금 화면에 선 개체\n${standing.length ? standing.join('\n') : '(없음)'}`, images: shot.images };
}

export async function runDemo(app: MathcoreApp, problem: string, think: boolean, signal: AbortSignal, hooks: Hooks, variant?: { elements: string[] }): Promise<string> {
  const startedAt = Date.now();
  let target = problem;
  // 사고는 풀이와 변형 작성에만 켠다. 도구 선택은 정해진 목록에서 고르는 일이라 끈다(소형 추론 모델은 과잉 사고로 맞은 호출을 덮어쓴다, ThinkBrake)
  const budget = think ? MAX_GENERATE_THINK : MAX_GENERATE;

  // 변형: 모델이 새 문항을 쓴다. 기준은 받은 기출 원문이다. 문항 패널에는 검증을 통과한 뒤에 낸다
  if (variant) {
    hooks.setStatus('변형 문항 작성 (호스트 모델)');
    const got = await askJson([{ role: 'system', content: variantPrompt(variant.elements) }, { role: 'user', content: `## 원본 문항\n${problem}` }], VARIANT_SCHEMA, think, signal, hooks, '변형', budget);
    target = cleanModelText(String(got.raw.problem ?? '')).trim();
    if (!target) throw new Error('변형 문항을 받지 못했다');
    const changes = Array.isArray(got.raw.changes) ? got.raw.changes.map((c) => String(c ?? '').trim()).filter(Boolean) : [];
    hooks.push('meta', `변형 ${seconds(got.ms)} · 생성 ${got.tokens ?? '?'} 토큰`);
    hooks.push('assistant', `변형 문항(검증 전):\n${target}`);
    hooks.setMeta({ mode: 'variant', variantText: problem, changes, similarity: similarityPercent(problem, target) });
  }

  // 표현하지 못한 요구(간극). 숨기지 않고 끝 관문의 판정에 싣는다
  const gaps: string[] = [];
  // 문항의 기호는 엔진이 문항의 식에서 읽는다. 그 기호가 값 선언의 키가 된다
  const textSymbols = problemSymbols(target, NAMES);
  if (textSymbols.length) hooks.push('meta', `문항의 기호 ${describeSymbols(textSymbols).join(', ')}`);

  // 추론 1: 풀이. 먼저 문법 강제 없이 글로 끝까지 풀고, 그 글에서 값(기호 값, 그림 유무, 답)을 JSON 으로 옮긴다. 값과 그림의 근거는 풀이 글이다
  // 배경 지식(기획 1항): 추론 전에 수능 형식 규칙과 유사 기출 참고를 먼저 준다. 출처와 정답은 주지 않는다
  const timeline: { label: string; at: number }[] = [];
  const mark = (label: string) => { timeline.push({ label, at: Date.now() - startedAt }); hooks.setMeta({ timeline: [...timeline] }); };
  const background = await backgroundKnowledge(target);
  hooks.setMeta({ background: { similar: background.similar, experience: background.experience } });
  hooks.push('meta', `배경 지식 · 수능 형식 규칙 · 유사 기출 참고 ${background.similar}개 · 이전 경험 레시피 ${background.experience.recipes}, 실패 ${background.experience.failures}, 선호 ${background.experience.notes} (저장 ${background.experience.total}건)`);
  mark('배경 지식');
  hooks.setStatus('1/5 추론 · 풀이와 그림 계획 (호스트 모델)');
  const wrote = await askText([{ role: 'system', content: `${writePrompt()}\n\n${background.text}` }, { role: 'user', content: target }], think, signal, hooks, '풀이와 그림 계획', budget);
  mark('풀이와 그림 계획');
  hooks.push('meta', `풀이와 그림 계획 ${seconds(wrote.ms)} · 생성 ${wrote.tokens ?? '?'} 토큰`);
  hooks.push('assistant', `풀이 글:\n${wrote.text}`);
  hooks.setStatus('2/5 추론 · 풀이 글에서 값 옮기기 (호스트 모델)');
  const solveSchemaFirst = solveSchema(textSymbols, [], LINK_TOOLS);
  // 그림이 필요한지는 문항이 정한다(곡선으로 읽히는 식, 이름 붙은 점). 문항과 어긋나는 「그림 없음」 자기 보고는 받지 않는다
  const needs = problemFigure(target);
  const targets = [...needs.curves.map((c) => `$${c}$`), ...needs.points.map((p) => `점 ${p}`)];
  const got1 = await askJson([{ role: 'system', content: extractPrompt(textSymbols) }, { role: 'user', content: `## 문항\n${target}\n\n## 풀이 글\n${wrote.text}` }], solveSchemaFirst, false, signal, hooks, '값 옮기기', MAX_GENERATE);
  mark('값 옮기기');
  const read1 = readSolved(got1.raw, textSymbols, wrote.text);
  // 관계 단언(그림의 기하학적 유효성). 끝에서 화면 좌표로 검사한다
  const relations: Relation[] = (Array.isArray(got1.raw.relations) ? got1.raw.relations as Record<string, unknown>[] : [])
    .filter((r) => r && ['on', 'meet', 'tangent', 'parallel', 'perpendicular'].includes(String(r.kind)) && typeof r.a === 'string' && typeof r.b === 'string')
    .map((r) => ({ kind: r.kind as Relation['kind'], a: String(r.a), b: String(r.b), c: typeof r.c === 'string' ? r.c : null }));
  if (relations.length) hooks.push('meta', `관계 단언 ${relations.length}개: ${relations.map((r) => `${r.kind}(${[r.a, r.b, r.c].filter(Boolean).join(',')})`).join(' ')}`);
  hooks.push('meta', `값 옮기기 ${seconds(got1.ms)} · 그림 ${read1.figure} · 기호 값 ${read1.values.map((v) => `${v.name}=${v.value}`).join(', ') || '없음'}${read1.unset.length ? ` (미정 ${read1.unset.join(', ')})` : ''} · 생성 ${got1.tokens ?? '?'} 토큰`);
  if (read1.figure === '없음' && targets.length) hooks.push('error', `문항에 그릴 대상(${targets.join(', ')})이 있어 「그림 없음」을 받지 않는다`);
  const solved: Solved = read1.figure === '없음' && targets.length ? { ...read1, figure: '있음' } : read1;
  for (const issue of solved.issues) hooks.push('error', issue);
  // 화면의 풀이는 모델이 쓴 풀이 글 그대로다. 옮긴 JSON 이 아니다
  const written = splitWritten(wrote.text);
  const declaredRead = readDeclarations(cleanModelText(normalizeDelimiters(written.steps)));
  let steps = declaredRead.text;
  let quick = cleanModelText(normalizeDelimiters(written.quick));
  const declared = declaredRead.declared;
  hooks.setMeta({ declared });
  hooks.push('meta', `그림 선언 ${declared.filter((d) => d.length).length}/${declared.length} 단계 · ${declared.map((d, i) => `${i + 1}:[${d.join(',')}]`).join(' ')}`);
  // 선언 이름 경계 캡처: 호출 하나가 끝날 때마다, 선언한 이름이 전부 선 풀이 단계를 찾아 그 순간을 찍는다
  const capturedSteps = new Set<number>();
  const afterEachCall = async (standingNames: string[], label: string): Promise<void> => {
    await hooks.captureFrame?.(standingNames, label);
    const have = new Set(standingNames);
    for (let i = 0; i < declared.length; i += 1) {
      if (capturedSteps.has(i + 1) || !declared[i].length) continue;
      if (declared[i].every((n) => have.has(n))) { capturedSteps.add(i + 1); await hooks.captureStep?.(i + 1, label, declared[i]); }
    }
  };
  let answer = solved.answer;
  let value = solved.value;
  hooks.push('assistant', `정답(모델): ${answer || '(없음)'}`);

  // 그림을 안 그리고 끝내는 경우의 마무리: 판정과 해설만 낸다
  const finish = (acceptance: Acceptance, reason: string, extra: string[], stampTail: string): string => {
    const elapsed = seconds(Date.now() - startedAt);
    if (variant) {
      if (acceptance === '성립') hooks.setMeta({ problem: target });
      else { hooks.setMeta({ rejected: reason }); hooks.push('error', `변형 문항 불성립: ${reason}. 검증을 통과한 문항만 낸다`); }
    }
    hooks.setMeta({ answer, acceptance, acceptanceReason: reason, elapsed });
    hooks.setSolution([
      `**판정: ${acceptance}** — ${reason}`, '',
      ...extra,
      SOLUTION_FORMAT.steps, '', steps || '(없음)', '',
      SOLUTION_FORMAT.quick, '', quick || '(없음)', '',
      `**정답:** ${answer || '(없음)'}`,
    ].join('\n'));
    const stamp = `${elapsed} · ${acceptance}${stampTail}`;
    hooks.push(acceptance === '불성립' ? 'error' : 'assistant', `끝. ${stamp}`);
    return stamp;
  };
  // 그림 없는 문항은 생성 단계를 돌리지 않는다. 잰 값이 없어 답을 대조하지 못하므로 미검증
  if (solved.figure === '없음') {
    const j0 = judge({ figure: '없음', failCount: 0, missingPoints: [], verdict: 'none', detail: '', answerIssue: answerFormatIssue(target, solved) });
    return finish(j0.acceptance, j0.reason, [SOLUTION_FORMAT.figure, '', '(그림 없는 문항)', ''], ' · 그림 없음');
  }

  // 제시된 식: 문항과 풀이 글의 식. 입력 단계와 연계 단계가 이 목록에서 고른다
  const given = givenWith(target, solved);
  // 값을 선언할 수 있는 기호: 문항의 기호와 풀이 글이 값을 정한 것. 풀이 글의 작업 변수(u, v, C 같은 것)는 포함하지 않는다
  const names = [...new Set([...solved.values.map((v) => v.name), ...textSymbols])];
  {
    // 제시된 식 가운데 expression 자리에 오르지 못한 것과 그 사유. 상주 페이지와 새 페이지의 식 목록이 달랐던 실측(2026-10-21 conformance S1 「AB」)을 좁히는 기록
    const split = splitGiven(given);
    const dropped = given.filter((g) => !split.curves.includes(g)).map((g) => { const r = readCurveShape(g); return `${g} (${r.problem ?? '사유 없음'})`; });
    hooks.push('meta', `제시된 식 ${given.length}개 · expression 후보 ${split.curves.length}개${dropped.length ? ` · 제외: ${dropped.join(' / ')}` : ''}`);
  }
  let drawn: Drawn = { rejected: [], results: [], skipped: [], issues: [] };
  const events: Event[] = [];
  let rounds = 0;
  const state: { last?: { current: Asked; transcript: ChatMessage[]; tools: string[] } } = {};
  const callsOf = (a: Asked): { tool?: unknown; args?: unknown }[] => (Array.isArray(a.raw.calls) ? a.raw.calls as { tool?: unknown; args?: unknown }[] : []);
  const standingText = (): string => sceneState(app).join('\n') || '(없음)';

  // 호출 JSON 을 받아 그리고, 반려가 있으면 커널 되먹임(반려 문장, 선 개체, 화면 그림)을 돌려 다시 받는다. 최대 FEEDBACK_ROUNDS 회. 단계마다 대화와 도구 집합이 따로다.
  // widen 이면 반려된 도구를 집합에 더하고 그 사용법을 되먹임에 함께 준다
  // 생성 단계마다 세운 호출(단계 설명의 재료). 호스트가 단계 설명을 못 쓰면 이것으로 코드가 채운다
  const stageNotes: { label: string; calls: string[] }[] = [];
  // 아직 안 선 이름을 가리킨 호출은 반려하지 않고 미뤄 두었다가, 다음 단계가 그 이름을 세운 뒤 다시 시도한다(단계 사이 순서. 실측 2026-10-19: 접선이 다음 단계의 점 Q 를 가리켜 반려 2건)
  const deferred: { tool: string; args: unknown; stage: string }[] = [];
  const DEFER_MARK = '아직 세워진 개체가 아니다';
  const retryDeferred = async (): Promise<{ ok: number; still: number }> => {
    if (!deferred.length) return { ok: 0, still: 0 };
    const again = await drawCalls(app, { calls: deferred.map((d) => ({ tool: d.tool, args: d.args })) }, true, given, [], (names) => afterEachCall(names, '미룬 호출'));
    const okNames = new Set(again.results.filter((r) => r.ok).map((r) => `${r.call.function.name}|${r.call.function.arguments}`));
    const before = deferred.length;
    for (let i = deferred.length - 1; i >= 0; i -= 1) { const d = deferred[i]; if (okNames.has(`${d.tool}|${JSON.stringify(d.args)}`)) deferred.splice(i, 1); }
    for (const r of again.results.filter((r) => r.ok)) { drawn.results.push(r); }
    if (before - deferred.length) hooks.push('meta', `미룬 호출 ${before - deferred.length}개를 뒤 단계에서 세움`);
    return { ok: before - deferred.length, still: deferred.length };
  };
  const callsStage = async (label: string, status: string, transcript: ChatMessage[], initial: string[], widen: boolean): Promise<void> => {
    let tools = initial;
    hooks.setStatus(`${status} (호스트 모델)`);
    const got = await askJson(transcript, callsSchema(given, names, tools, true, standingRefs(app)), false, signal, hooks, label, MAX_GENERATE_TOOLS);
    const merged = withValues(got.raw, solved, names);
    let cur: Asked = { ...got, raw: merged.raw };
    let symbolIssues = merged.issues;
    hooks.push('meta', `${label} ${seconds(cur.ms)} · 호출 ${callsOf(cur).length}개 · 기호 값 ${Object.entries(readSymbols(cur.raw, names)).map(([k, v]) => `${k}=${v}`).join(', ') || '없음'} · 생성 ${cur.tokens ?? '?'} 토큰`);
    for (const c of callsOf(cur)) hooks.push('call', `${String(c.tool)} ${JSON.stringify(c.args ?? {})}`);
    // 생성: 코드가 dsl.json 으로 대조하고 mathcore 에 세운다. 되먹임은 화면을 지우지 않고 이어 그린다. 판정은 생성 결과뿐
    const draw = async (): Promise<void> => {
      const drawnNow = await drawCalls(app, cur.raw, true, given, [], (names) => afterEachCall(names, label.replace(' 사용법과 args', '')));
      // 아직 안 선 이름 참조는 미룬다. 반려 목록에서 빼고 뒤 단계에서 다시 시도한다
      const held = drawnNow.rejected.filter((r) => r.detail.includes(DEFER_MARK));
      for (const h of held) { const c = callsOf(cur).find((x) => String(x.tool) === h.tool && (typeof (x.args as Record<string, unknown>)?.name !== 'string' || (x.args as Record<string, unknown>).name === h.name)); if (c) deferred.push({ tool: h.tool, args: c.args, stage: label }); }
      if (held.length) hooks.push('meta', `${label}: 아직 안 선 개체를 가리킨 호출 ${held.length}개를 뒤 단계로 미룸`);
      const more = { ...drawnNow, rejected: drawnNow.rejected.filter((r) => !held.includes(r)) };
      stageNotes.push({ label: label.replace(' 사용법과 args', ''), calls: more.results.filter((r) => r.ok).map((r) => `${r.call.function.name} ${r.call.function.arguments}`) });
      mark(label.replace(' 사용법과 args', ''));
      await retryDeferred();
      await hooks.afterExecute?.(label);
      logDrawn(hooks, more);
      events.push(...eventsOf(more));
      drawn = { rejected: more.rejected, results: [...drawn.results.filter((r) => r.ok), ...more.results], skipped: more.skipped, issues: more.issues };
    };
    await draw();
    for (let round = 0; round < FEEDBACK_ROUNDS; round += 1) {
      const complaints = complaintsOf(drawn, symbolIssues);
      if (!complaints.length) break;
      const widened = widen ? inPalette([...tools, ...failedToolsOf(drawn)]) : tools;
      const added = widened.filter((t) => !tools.includes(t));
      tools = widened;
      hooks.setStatus(`${status} · 반려를 돌려주고 다시 받음 ${round + 1}/${FEEDBACK_ROUNDS} (호스트 모델)`);
      transcript.push({ role: 'assistant', content: JSON.stringify(cur.raw) });
      transcript.push(feedbackTurn(app, hooks, `아래 호출이 반려됐다. 지금 화면에 선 개체는 그대로 남아 있으니 다시 세우지 않는다. 반려된 것을 고친 호출과 더 그릴 호출만 같은 스키마의 JSON 으로 내라. 가리키는 이름은 선 개체이거나 이 JSON 에서 먼저 세운 것이어야 한다.\n${complaints.map((c) => `- ${c}`).join('\n')}${added.length ? `\n\n## 추가된 색칠도구의 사용법\n${paletteText(added)}` : ''}`));
      const again = await askJson(transcript, callsSchema(given, names, tools, true, standingRefs(app)), false, signal, hooks, `${label} 되묻기 ${round + 1}`, MAX_GENERATE_TOOLS);
      const mergedAgain = withValues(again.raw, solved, names);
      cur = { ...again, raw: mergedAgain.raw };
      symbolIssues = mergedAgain.issues;
      hooks.push('meta', `되묻기 ${round + 1} ${seconds(cur.ms)} · 호출 ${callsOf(cur).length}개 · 생성 ${cur.tokens ?? '?'} 토큰`);
      for (const c of callsOf(cur)) hooks.push('call', `${String(c.tool)} ${JSON.stringify(c.args ?? {})}`);
      await draw();
      rounds += 1;
    }
    state.last = { current: cur, transcript, tools };
  };

  // 추론 3: 입력 개체. 값만 받는 도구(식, 좌표, 슬라이더)로 곡선과 점을 세운다. 모델이 계산한 좌표를 코드가 먼저 찍지 않는다
  const inputTools = names.length ? INPUT_TOOLS : INPUT_TOOLS.filter((t) => t !== 'etc.slider');
  const inputSystem = inputPrompt(given, names, inputTools);
  const withShot = (m: ChatMessage): ChatMessage => { const shot = hooks.latestShot?.(); return shot ? { ...m, images: [shot] } : m; };
  await callsStage('입력 개체', '3/5 추론 · 입력 개체', [{ role: 'system', content: inputSystem }, withShot({ role: 'user', content: `## 문항\n${target}\n\n## 풀이\n${wrote.text}` })], inputTools, false);

  // 연계 도구는 풀이 작문 단계의 「그림 계획」이 정했고 값 옮기기가 그 이름을 옮겼다. 별도의 취합 단계는 없다
  const picked = readPicked({ tools: solved.tools }, LINK_TOOLS);
  hooks.push('meta', `그림 계획의 연계 도구 ${picked.length}개: ${picked.join(', ') || '없음'}`);

  // 추론 5: 연계 사용법과 args. 취합한 도구를 의존 순서의 단계(구성, 측정, 꾸미기)로 나눠, 한 단계가 끝나 개체가 선 뒤에 다음 단계를 돌린다.
  // 단계마다 그 단계 도구의 사용법만 받고 고정 JSON 하나를 낸다. 식은 제시된 것, 이미 선 개체는 id 로 가리킨다
  // 단계마다 고른 도구가 받는 종류를 세우는 도구(점, 선분 등)를 함께 싣는다. 받는 개체를 세울 길이 없어 되묻기로 가던 구멍을 막는다
  const phases = LINK_PHASES.map((p) => { const own = picked.filter((t) => p.tools.includes(t)); return { key: p.key, tools: withProducers(own), own }; }).filter((p) => p.own.length);
  for (const p of phases) { const added = p.tools.filter((t) => !p.own.includes(t)); if (added.length) hooks.push('meta', `${p.key} 단계에 선행 도구 추가: ${added.join(', ')}`); }
  const phaseSystems = phases.map((p) => argsPrompt(given, names, p.tools, p.key));
  hooks.setMeta({ fixed: { schemaSha: await sha256(JSON.stringify(callsSchema(given, names, picked, true))), solveSchemaSha: await sha256(JSON.stringify(solveSchemaFirst)), inputPromptSha: await sha256(inputSystem), writePromptSha: await sha256(writePrompt()), promptSha: await sha256(phaseSystems.join('\n---\n')), given, textSymbols, symbols: names, tools: picked } });
  if (!phases.length) {
    hooks.push('meta', '취합한 연계 도구가 없어 연계 단계를 건너뛴다');
  } else {
    hooks.push('meta', `연계 단계 ${phases.map((p) => `${p.key}(${p.tools.length})`).join(' 다음 ')}`);
    for (const [k, phase] of phases.entries()) {
      await callsStage(`${phase.key} 사용법과 args`, `4/5 추론 · ${phase.key} 사용법과 args`, [{ role: 'system', content: phaseSystems[k] }, withShot({ role: 'user', content: `## 문항\n${target}\n\n## 풀이\n${wrote.text}\n\n## 이미 선 개체(지금 화면 그림을 함께 본다)\n${standingText()}` })], phase.tools, true);
    }
  }

  // 끝까지 세우지 못한 미룬 호출은 실패로 센다
  if (deferred.length) {
    for (const d of deferred) { hooks.push('error', `미룬 호출을 끝까지 세우지 못함 · ${d.tool} ${JSON.stringify(d.args)} (${d.stage}): 가리킨 개체가 서지 않았다`); events.push({ ok: false, tool: d.tool, name: typeof (d.args as Record<string, unknown>)?.name === 'string' ? String((d.args as Record<string, unknown>).name) : '' }); }
  }

  // 해설 정리(집결): 단계는 따로 돌지만 풀이에서 모인다(사용자 지시 2026-10-21). 그린 개체의 이름과 잰 값을 주고,
  // 단계 설명과 정석 풀이, 실전 풀이를 그 이름(점 A, 접선 l1, 넓이 m2)으로 다시 받는다. 수학 내용은 바꾸지 않고 인용만 더한다. 비면 원래 글을 쓴다
  let measured = readMeasurements(app);
  if (stageNotes.length) {
    hooks.setStatus('해설 정리 (호스트 모델)');
    const registry = registryOf(app);
    const objectLines = registry.knownNames().map((n) => { const o = registry.objectOf(n); if (!o) return ''; const kind = kindLabel(String(o.type ?? '')); const pos = Array.isArray(o.position) && o.position.every((v) => Number.isFinite(v)) ? ` (${o.position.map((v) => Math.round(v * 1000) / 1000).join(', ')})` : ''; return `- ${n}: ${kind}${pos}`; }).filter(Boolean);
    const fallback = stageNotes.map((n) => `${n.label}: ${n.calls.map((c) => c.split(' ')[0]).join(', ') || '없음'}`);
    const stageList = stageNotes.map((n, i) => `${i + 1}. ${n.label}\n${n.calls.map((c) => `   - ${c}`).join('\n') || '   - (세운 개체 없음)'}`).join('\n');
    const measuredText = measured.map((m) => `${m.kind} ${m.name} = ${m.value}`).join(', ') || '없음';
    try {
      const got = await askJson([
        { role: 'system', content: [
          '그림이 끝났다. 이제 풀이를 그림에 맞춰 정리한다. 아래 「그린 개체」의 이름을 풀이와 단계 설명에서 그대로 인용한다(예: 점 A, 접선 l1, 넓이 m2). 목록에 없는 이름은 쓰지 않는다.',
          'captions: 그림 단계마다 무엇을 그렸고 무엇을 쟀는지, 풀이 흐름에서 그 단계의 뜻을 한두 문장으로. 단계 순서대로, 개수는 단계 수와 같다.',
          'steps: 정석 풀이 본문을 다시 쓴다. 수학 내용과 단계 수는 원래 풀이를 지키고, 개체 이름과 잰 값을 인용해 그림과 글이 맞게 한다. 번호 「1. 」로 시작하는 줄들이다.',
          'quick: 실전 풀이 본문을 같은 방식으로 다시 쓴다.',
          'mapping: 정석 풀이 단계 번호마다 그 단계의 내용을 보여 주는 그림 단계 번호를 적는다(해설과 삽화의 연결). 그 풀이 단계 문장에 그 그림 단계가 세운 개체 이름이 있어야 한다. 그림이 없는 풀이 단계는 빼도 된다.',
          WRITING_RULE, '그림에 없는 값을 지어내지 않는다. 잰 값은 아래 「잰 값」의 수를 그대로 쓴다.',
        ].join('\n') },
        { role: 'user', content: `## 문항\n${target}\n\n## 원래 풀이\n${SOLUTION_FORMAT.steps}\n${steps}\n\n${SOLUTION_FORMAT.quick}\n${quick}\n\n## 그린 개체\n${objectLines.join('\n') || '(없음)'}\n\n## 그림 단계와 세운 호출\n${stageList}\n\n## 잰 값\n${measuredText}` },
      ], { type: 'object', properties: { captions: { type: 'array', minItems: stageNotes.length, maxItems: stageNotes.length, items: { type: 'string', maxLength: 400 } }, steps: { type: 'string', maxLength: 6000 }, quick: { type: 'string', maxLength: 3000 }, mapping: { type: 'array', description: '정석 풀이 단계(1부터)마다 그 단계를 보여 주는 그림 단계 번호(1부터). 그림이 없는 풀이 단계는 적지 않는다', maxItems: 20, items: { type: 'object', properties: { step: { type: 'integer', minimum: 1 }, stage: { type: 'integer', minimum: 1, maximum: Math.max(1, stageNotes.length) } }, required: ['step', 'stage'], additionalProperties: false } } }, required: ['captions', 'steps', 'quick', 'mapping'], additionalProperties: false }, false, signal, hooks, '해설 정리', MAX_GENERATE_TOOLS);
      const raw = Array.isArray(got.raw.captions) ? got.raw.captions.map((c) => cleanModelText(normalizeDelimiters(String(c ?? ''))).trim()) : [];
      const captions = stageNotes.map((_, i) => raw[i] || fallback[i]);
      const newSteps = readDeclarations(cleanModelText(normalizeDelimiters(String(got.raw.steps ?? '')))).text.trim();
      const newQuick = cleanModelText(normalizeDelimiters(String(got.raw.quick ?? ''))).trim();
      const cited = [...objectLines.map((l) => l.slice(2).split(':')[0])].filter((n) => newSteps.includes(n)).length;
      if (newSteps) steps = newSteps;
      if (newQuick) quick = newQuick;
      // 매핑 검증: 매핑된 그림 단계가 세운 개체 이름이 그 풀이 단계 문장에 실제로 있어야 한다. 없으면 버리고 사유를 남긴다
      const stepLines = steps.split('\n').filter((l) => /^\d+\.\s/.test(l.trim()));
      const stageNames = stageNotes.map((n) => n.calls.map((c) => { try { const a = JSON.parse(c.slice(c.indexOf(' ') + 1)); return typeof a.name === 'string' ? a.name : ''; } catch { return ''; } }).filter(Boolean));
      const rawMap = Array.isArray(got.raw.mapping) ? got.raw.mapping as { step?: unknown; stage?: unknown }[] : [];
      const stepMap: { step: number; stage: number }[] = [];
      const dropped: string[] = [];
      for (const m of rawMap) {
        const step = Number(m.step); const stage = Number(m.stage);
        if (!Number.isInteger(step) || !Number.isInteger(stage) || step < 1 || step > stepLines.length || stage < 1 || stage > stageNotes.length) { dropped.push(`${String(m.step)}→${String(m.stage)} 범위 밖`); continue; }
        const line = stepLines[step - 1];
        const names = stageNames[stage - 1];
        // 이름은 낱말 경계로 찾되, 수식($…$) 안에서는 \overrightarrow{AB}, ABPC 처럼 붙어 쓰이므로 포함으로 본다
        const mathParts = (line.match(/\$[^$]*\$/g) ?? []).join(' ');
        const cites = (n: string): boolean => new RegExp(`(^|[^A-Za-z0-9_])${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^A-Za-z0-9_]|$)`).test(line) || (n.length <= 2 && mathParts.includes(n));
        if (names.length && !names.some(cites)) { dropped.push(`${step}→${stage} 이름 없음(${names.join(',')})`); continue; }
        if (!stepMap.some((x) => x.step === step)) stepMap.push({ step, stage });
      }
      if (!stepMap.length) for (let i = 0; i < Math.min(stepLines.length, stageNotes.length); i += 1) stepMap.push({ step: i + 1, stage: i + 1 });
      // 선언이 있으면 매핑은 코드가 정한 단계 캡처(captureStep)가 우선이다. stepMap 은 단계 캡처가 없는 풀이 단계의 보조다
      hooks.setMeta({ stageCaptions: captions, stepMap });
      hooks.push('meta', `선언 단계 캡처 ${capturedSteps.size}/${declared.filter((d) => d.length).length}`);
      if (dropped.length) hooks.push('meta', `매핑에서 버림 ${dropped.length}건: ${dropped.join(' / ')}`);
      mark('해설 정리');
      hooks.push('meta', `해설 정리 ${seconds(got.ms)} · 단계 설명 ${captions.length}개 · 풀이 ${newSteps ? '다시 씀' : '원문 유지'} · 인용한 개체 ${cited}/${objectLines.length}${raw.filter(Boolean).length < stageNotes.length ? ' · 빈 설명은 호출 목록으로 채움' : ''}`);
    } catch (error) {
      hooks.setMeta({ stageCaptions: fallback, stepMap: stageNotes.map((_, i) => ({ step: i + 1, stage: i + 1 })) });
      hooks.push('error', `해설 정리를 받지 못해 원래 풀이와 호출 목록을 쓴다: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // 엔진 측정: 문항이 구하라는 양(target)은 호스트의 measure 호출과 별개로 엔진이 직접 세워 잰다. 호스트가 잰 값이 이미 맞으면 세우지 않는다
  let extraValues: number[] = [];
  if (solved.target.kind !== 'none' && verifyAgainstMeasured(value, measured.map((m) => m.value), solved.measureValue).verdict !== 'ok') {
    const built = await buildTarget(app, solved.target, drawn.results, (names) => afterEachCall(names, '엔진 측정'));
    hooks.push(built.values.length ? 'meta' : 'error', `엔진 측정 · ${built.note}${built.values.length ? ` · 값 ${built.values.map((v) => `${v.kind} ${v.value}`).join(', ')}` : ''}`);
    if (built.drawn) {
      logDrawn(hooks, built.drawn);
      drawn = { rejected: [...drawn.rejected, ...built.drawn.rejected], results: [...drawn.results, ...built.drawn.results], skipped: [...drawn.skipped, ...built.drawn.skipped], issues: [...drawn.issues, ...built.drawn.issues] };
      await hooks.afterExecute?.('엔진 측정');
      measured = readMeasurements(app);
      extraValues = built.values.map((v) => v.value);
    }
  }
  // 검증: 모델 답을 도구가 잰 값과 대조한다. 다르면 판정 사유로 남긴다. 되돌려 묻지 않는다(되묻기 없음, 사용자 명령 2026-10-19)
  const VERIFY_ROUNDS = 0;
  let check = verifyAgainstMeasured(value, [...measured.map((m) => m.value), ...extraValues], solved.measureValue);
  let verifyRound = 0;
  const last = state.last;
  if (VERIFY_ROUNDS > 0 && check.verdict === 'mismatch' && last) {
    verifyRound = 1;
    hooks.setStatus('검증 · 잰 값과 답이 달라 되돌려 묻는다 (호스트 모델)');
    hooks.push('error', `검증 불일치: ${check.detail}`);
    last.transcript.push({ role: 'assistant', content: JSON.stringify(last.current.raw) });
    last.transcript.push(feedbackTurn(app, hooks, `도구가 잰 값과 네 답이 다르다. ${check.detail}.\n잰 값: ${measured.map((m) => `${m.kind} ${registryOf(app).aliasOf(m.id)} = ${m.value}`).join(', ')}\n네 답(value): ${value}\n재는 호출이 잘못됐으면(다른 개체를 쟀거나 재야 할 것을 안 쟀으면) 고친 호출과 더 잴 호출을 calls 에, 답이 틀렸으면 고친 수를 value 에 낸다. 둘 다 맞다고 보면 calls 는 비우고 value 는 같은 값으로 낸다.`));
    const again = await askJson(last.transcript, verifySchema(given, names, last.tools, standingRefs(app)), false, signal, hooks, '검증 되묻기', MAX_GENERATE_TOOLS);
    const redo: Asked = { ...again, raw: withValues(again.raw, solved, names).raw };
    const redoCalls = callsOf(redo);
    const revised = typeof redo.raw.value === 'number' && Number.isFinite(redo.raw.value) ? redo.raw.value : null;
    hooks.push('meta', `검증 되묻기 ${seconds(redo.ms)} · 호출 ${redoCalls.length}개 · 답 ${revised === null ? '(없음)' : revised} · 생성 ${redo.tokens ?? '?'} 토큰`);
    for (const c of redoCalls) hooks.push('call', `${String(c.tool)} ${JSON.stringify(c.args ?? {})}`);
    if (redoCalls.length) {
      const more = await drawCalls(app, redo.raw, true, given, [], (names) => afterEachCall(names, '검증 되묻기'));
      await hooks.afterExecute?.('검증 되묻기');
      logDrawn(hooks, more);
      events.push(...eventsOf(more));
      drawn = { rejected: more.rejected, results: [...drawn.results.filter((r) => r.ok), ...more.results], skipped: more.skipped, issues: more.issues };
    }
    if (revised !== null && revised !== value) { value = revised; answer = answerText(solved.choice, value); hooks.push('meta', `모델이 답을 고쳤다: ${solved.answer || '(없음)'} → ${answer}`); }
    measured = readMeasurements(app);
    check = verifyAgainstMeasured(value, measured.map((m) => m.value), solved.measureValue);
  }
  // 그림 「있음」인데 개체를 하나도 못 세웠으면 표현하지 못한 요구로 판정에 싣는다
  if (!drawn.results.some((r) => r.ok)) gaps.push('그릴 개체를 하나도 세우지 못함');
  const okCount = drawn.results.filter((r) => r.ok).length;
  const failCount = unresolvedFailures(events);
  const verdict = check.verdict === 'ok' ? `✓ ${check.detail}` : check.verdict === 'mismatch' ? `✗ ${check.detail}` : SOLUTION_FORMAT.verifyNone;
  // 끝 관문: 원문과 변형 모두 같은 판정. 문항이 이름 붙인 점이 점으로 전부 섰는지(개체 포함), 실패 0, 답 대조
  // 문항의 점 이름은 모델이 요청한 이름으로 세운 점으로만 센다(mathcore 가 자동으로 붙인 이름 A, B 와 섞이지 않게 id 로 간 등록부로 확인한다)
  const registry = registryOf(app);
  const standingPoints = new Set(registry.knownNames().filter((n) => registry.objectOf(n)?.type === 'point'));
  const missingPoints = problemPointNames(target).filter((n) => !standingPoints.has(n));
  // 관계 단언 검사: 실패는 판정 사유(불성립)로, 검사 불가는 기록만
  const relationResults = relations.length ? checkRelations(app, relations, provenanceOf(app, drawn.results)) : [];
  for (const r of relationResults) {
    const name = `${r.relation.kind}(${[r.relation.a, r.relation.b, r.relation.c].filter(Boolean).join(',')})`;
    if (r.verdict === 'fail') { gaps.push(`관계 ${name} 어긋남: ${r.detail}`); hooks.push('error', `관계 단언 실패 · ${name}: ${r.detail}`); }
    else hooks.push('meta', `관계 단언 ${r.verdict === 'ok' ? '통과' : '검사 불가'} · ${name}: ${r.detail}`);
  }
  if (relationResults.length) hooks.setMeta({ relations: relationResults.map((r) => ({ kind: r.relation.kind, a: r.relation.a, b: r.relation.b, c: r.relation.c ?? null, verdict: r.verdict, detail: r.detail })) });
  // 독립 재계산: 호스트가 잰 값과 무관하게 코드가 구하는 양을 장면에서 다시 계산해 맞출 값(잴 값, 없으면 답)과 비교한다
  const independent = judgeIndependent(independentValue(app, solved.target, drawn.results), solved.measureValue ?? value);
  hooks.push(independent.verdict === 'mismatch' ? 'error' : 'meta', `독립 재계산 ${independent.verdict} · ${independent.detail}`);
  hooks.setMeta({ independent });
  const j = judge({ figure: '있음', failCount, missingPoints, verdict: check.verdict, detail: check.detail, gaps, answerIssue: answerFormatIssue(target, { choice: solved.choice, value }), answerMissing: value === null && !solved.choice, independent });
  if (variant) {
    if (j.acceptance === '성립') hooks.setMeta({ problem: target });
    else { hooks.setMeta({ rejected: j.reason }); hooks.push('error', `변형 문항 불성립: ${j.reason}. 검증을 통과한 문항만 낸다`); }
  }
  const figureLines = drawn.results.filter((r) => r.ok).map((r) => `- ${r.call.function.name} ${r.call.function.arguments}`);
  const elapsed = seconds(Date.now() - startedAt);
  hooks.setMeta({ answer, verdict, acceptance: j.acceptance, acceptanceReason: j.reason, measured: measured.map((m) => `- ${m.kind} ${m.name}: ${m.value}`), figure: figureLines.join('\n'), objectCount: okCount, failureCount: failCount, elapsed });
  hooks.setSolution([
    `**판정: ${j.acceptance}** — ${j.reason}`, '',
    SOLUTION_FORMAT.figure, '', figureLines.join('\n') || '(세운 개체 없음)', '',
    SOLUTION_FORMAT.steps, '', steps || '(없음)', '',
    SOLUTION_FORMAT.quick, '', quick || '(없음)', '',
    `**정답:** ${answer || '(없음)'}`, '', `**${verdict}**`,
  ].join('\n'));
  const stamp = `${elapsed} · ${j.acceptance} · 개체 ${okCount}${failCount ? ` · 실패 ${failCount}` : ''}${missingPoints.length ? ` · 누락 ${missingPoints.join(',')}` : ''}${rounds ? ` · 되먹임 ${rounds}회` : ''}${verifyRound ? ' · 검증 되묻기 1회' : ''}`;
  hooks.push(j.acceptance === '불성립' ? 'error' : 'assistant', `끝. ${stamp}`);
  return stamp;
}
